import { useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarDays } from "lucide-react";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { getISOWeek } from "date-fns";
import { format } from "date-fns";
import { nb } from "date-fns/locale";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  childId: string;
  familyId: string;
  onSuccess?: () => void;
}

export function AddHomeworkSheet({ open, onOpenChange, childId, familyId, onSuccess }: Props) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [title, setTitle] = useState("");
  const [dueDate, setDueDate] = useState<Date | null>(null);
  const [saving, setSaving] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = title.trim();
    if (!trimmed || !childId || !familyId) return;
    setSaving(true);
    try {
      const weekNumber = getISOWeek(new Date());
      const fingerprint = `manual-${childId}-${Date.now()}`;
      const { data: hwData, error: hwErr } = await supabase
        .from("homework" as any)
        .insert({
          child_id: childId,
          family_id: familyId,
          week_number: weekNumber,
          subject: null,
          title: trimmed,
          due_date: dueDate ? format(dueDate, "yyyy-MM-dd") : null,
          scope: "flexible_week",
          confidence: null,
          source_fingerprint: fingerprint,
          import_batch_id: null,
          created_by: user?.id ?? null,
          original_text: null,
        } as any)
        .select("id")
        .single();
      if (hwErr) throw hwErr;
      if (hwData) {
        await supabase.from("homework_subtasks" as any).insert({
          homework_id: (hwData as any).id,
          title: trimmed,
          completed: false,
          sort_order: 0,
        } as any);
      }
      queryClient.invalidateQueries({ queryKey: ["homework", childId] });
      toast.success("Lekse lagt til");
      setTitle("");
      setDueDate(null);
      onOpenChange(false);
      onSuccess?.();
    } catch (err) {
      toast.error("Kunne ikke legge til lekse");
      console.warn(err);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="rounded-t-2xl">
        <SheetHeader>
          <SheetTitle>Legg til lekse</SheetTitle>
          <SheetDescription>
            Skriv inn tittel og valgfri frist. Lekse opprettes med én deloppgave.
          </SheetDescription>
        </SheetHeader>
        <form onSubmit={handleSubmit} className="space-y-4 pt-4">
          <div className="space-y-2">
            <Label htmlFor="hw-title">Tittel</Label>
            <Input
              id="hw-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="F.eks. Les kapittel 3"
              required
              maxLength={200}
            />
          </div>
          <div className="space-y-2">
            <Label>Frist (valgfri)</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  className={cn("w-full justify-start text-left font-normal", !dueDate && "text-muted-foreground")}
                >
                  <CalendarDays className="mr-2 h-4 w-4" />
                  {dueDate ? format(dueDate, "d. MMMM yyyy", { locale: nb }) : "Velg dato"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar mode="single" selected={dueDate ?? undefined} onSelect={(d) => setDueDate(d ?? null)} initialFocus />
              </PopoverContent>
            </Popover>
          </div>
          <div className="flex gap-2 pt-2">
            <Button type="button" variant="outline" className="flex-1" onClick={() => onOpenChange(false)}>
              Avbryt
            </Button>
            <Button type="submit" className="flex-1" disabled={saving || !title.trim()}>
              {saving ? "Legger til…" : "Legg til"}
            </Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
}
